import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Checkbox } from "../../../global/checkbox/Checkbox";
import {
  removeAllFilters,
  setIdFilters,
  setTitleFilters,
} from "../../../../redux/filtersSlice";
import { IDrawerFiltersItems } from "../../../../interfaces/globalComponents/layout/material/NavigationDrawer/IDrawerFilters";
import { SelectedFiltersHeader } from "./SelectedFiltersHeader";
import { PrimaryButton } from "../../../global/buttons/material/primaryButton/PrimaryButton";
import { OutlinedButton } from "../../../global/buttons/material/outlinedButton/OutlinedButton";
import { setOpenModal } from "../../../../redux/pages/exploreSlice";
import { TextButton } from "../../../global/buttons/material/textButton/TextButton";
import { IFilterList } from "../../../../interfaces/globalComponents/explore/material/exploreAllFilters/IFiltersList";
import { IconButton } from "../../../global/buttons/material/iconButton/IconButton";
import { Close } from "@mui/icons-material";

export const FiltersList = ({ filterFor, type, className }: IFilterList) => {
  const componentColors = useSelector(
    (state: RootState) => state.theme.componentColors
  );
  const selectedFilters = useSelector((state: RootState) => state.filters);
  const exploreFilters = useSelector(
    (state: RootState) => state.explore.value.filtersList
  );
  const dispatch = useDispatch();

  const handleSelectObjective = (objective: IDrawerFiltersItems) => {
    dispatch(
      setIdFilters({
        id: objective.id,
        type: "objectives",
        filterFor: filterFor,
      })
    );
  };

  const handleSelectSport = (sport: IDrawerFiltersItems) => {
    dispatch(
      setIdFilters({
        id: sport.id,
        type: "sports",
        filterFor: filterFor,
      })
    );
  };

  const handleSelectLevel = (level: IDrawerFiltersItems) => {
    dispatch(
      setTitleFilters({
        title: level.title,
        type: "levels",
        filterFor: filterFor,
      })
    );
  };

  const handleSelectInterests = (interest: IDrawerFiltersItems) => {
    dispatch(
      setTitleFilters({
        title: interest?.title,
        type: "interests",
        filterFor: filterFor,
      })
    );
  };

  const handleClose = () => {
    dispatch(
      setOpenModal({
        bottomsheet: false,
        menu: false,
        drawer: false,
      })
    );
  };

  const handleClearResults = () => {
    dispatch(removeAllFilters({ filterFor, type }));
  };

  return (
    <div className="rounded-[28px]">
      <div className={`${className}  lg:h-[390px] overflow-y-scroll`}>
        <Stack direction={"row"} className="justify-between lg:hidden">
          <Typography
            sx={{
              color: componentColors.surface.onSurface,
            }}
            className="p-4 font-medium"
          >
            Filters
          </Typography>

          <IconButton
            onClick={handleClose}
            icon={<Close sx={{ color: componentColors.surface.onSurface }} />}
          />
        </Stack>

        <SelectedFiltersHeader filterFor={filterFor} type={type} />
        {type == "interests" && (
          <>
            <Typography className="pl-4 pt-4 font-medium">Expertise</Typography>
            <List>
              {exploreFilters?.interests?.map((item) => (
                <ListItemButton
                  onClick={() => handleSelectInterests(item)}
                  key={item.id}
                >
                  <ListItem className="flex justify-between p-0">
                    <ListItemText
                      sx={{
                        "& .MuiTypography-root": {
                          color: componentColors.surface.onSurface,
                        },
                      }}
                    >
                      {item.title}
                    </ListItemText>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedFilters[filterFor].interests.includes(
                          item.value.toLowerCase()
                        )}
                      />
                    </ListItemIcon>
                  </ListItem>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
        {type === "objectives" && (
          <>
            <Typography className="pl-4 pt-4 font-medium">
              Objectives
            </Typography>
            <List>
              {exploreFilters?.objectives?.map((item) => (
                <ListItemButton
                  onClick={() => handleSelectObjective(item)}
                  key={item.id}
                >
                  <ListItem className="flex justify-between p-0">
                    <ListItemText
                      sx={{
                        "& .MuiTypography-root": {
                          color: componentColors.surface.onSurface,
                        },
                      }}
                    >
                      {item.title}
                    </ListItemText>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedFilters[filterFor].objectives.includes(
                          item.id
                        )}
                      />
                    </ListItemIcon>
                  </ListItem>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
        {type === "sports" && (
          <>
            <Typography className="pl-4 pt-4 font-medium">Sports</Typography>
            <List>
              {exploreFilters?.sports?.map((item) => (
                <ListItemButton
                  onClick={() => handleSelectSport(item)}
                  key={item.id}
                >
                  <ListItem className="flex justify-between p-0">
                    <ListItemText
                      sx={{
                        "& .MuiTypography-root": {
                          color: componentColors.surface.onSurface,
                        },
                      }}
                    >
                      {item.title}
                    </ListItemText>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedFilters[filterFor].sports.includes(
                          item.id
                        )}
                      />
                    </ListItemIcon>
                  </ListItem>
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {type === "levels" && (
          <>
            <Typography className="pl-4 pt-4 font-medium">Level</Typography>
            <List>
              {exploreFilters?.levels?.map((item) => (
                <ListItemButton
                  onClick={() => handleSelectLevel(item)}
                  key={item.id}
                >
                  <ListItem className="flex justify-between p-0">
                    <ListItemText
                      sx={{
                        "& .MuiTypography-root": {
                          color: componentColors.surface.onSurface,
                        },
                      }}
                    >
                      {item.title}
                    </ListItemText>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedFilters[filterFor].levels.includes(
                          item.title.toLowerCase()
                        )}
                      />
                    </ListItemIcon>
                  </ListItem>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </div>

      <Stack
        sx={{
          borderTop: "1px solid",
          borderColor: componentColors.outline.outline,
        }}
        className="flex flex-col rounded-b-[28px] pb-10 p-4 md:p-6  md:flex-row md:space-y-0 items-center justify-between"
      >
        <div className=" flex flex-col md:flex-row lg:flex-row-reverse md:space-y-0 md:space-x-2 lg:items-center w-full space-y-2">
          <PrimaryButton
            title="Apply"
            onClick={handleClose}
            className="lg:ml-2 lg:flex-1 "
          />
          <OutlinedButton
            title="Cancel"
            onClick={handleClose}
            borderColor={componentColors.outline.outline}
          />
        </div>

        <TextButton
          text="Clear results"
          className="hidden md:flex lg:hidden w-36"
          onClick={handleClearResults}
        />
      </Stack>
    </div>
  );
};
